import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { generateAndStoreThumbnail } from '@/utils/thumbnailGenerator';
import { ArrowDown, ArrowUp, Image, Loader2, Star, Trash2, UploadCloud } from 'lucide-react';

interface ImageUploadProps {
  eventId: string;
  onUploadComplete?: () => void;
}

interface PendingImage {
  id: string;
  file: File;
  previewUrl: string;
  altText: string;
  captionEn: string;
  captionKo: string;
  isCover: boolean;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const cleanFileName = (fileName: string) => fileName.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');

const ImageUpload = ({ eventId, onUploadComplete }: ImageUploadProps) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);

  const validateFile = (file: File) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return 'Unsupported format. Please upload JPG, PNG, or WebP.';
    if (file.size > MAX_FILE_SIZE) return 'Image is too large. Please upload an image under 2MB.';
    return null;
  };

  const addFiles = (files: FileList | File[]) => {
    const currentHasCover = pendingImages.some((image) => image.isCover);
    const validImages: PendingImage[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
        return;
      }

      validImages.push({
        id: crypto.randomUUID(),
        file,
        previewUrl: URL.createObjectURL(file),
        altText: cleanFileName(file.name),
        captionEn: '',
        captionKo: '',
        isCover: !currentHasCover && pendingImages.length === 0 && validImages.length === 0,
      });
    });

    if (errors.length > 0) {
      toast({
        title: 'Some images were skipped',
        description: errors.slice(0, 3).join('\n'),
        variant: 'destructive',
      });
    }

    if (validImages.length > 0) setPendingImages((current) => [...current, ...validImages]);
  };

  const clearAll = () => {
    pendingImages.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    setPendingImages([]);
  };

  const removeImage = (id: string) => {
    setPendingImages((current) => {
      const removed = current.find((image) => image.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      const next = current.filter((image) => image.id !== id);
      if (!next.some((image) => image.isCover) && next.length > 0) return next.map((image, index) => ({ ...image, isCover: index === 0 }));
      return next;
    });
  };

  const updateImage = (id: string, changes: Partial<PendingImage>) => {
    setPendingImages((current) => current.map((image) => image.id === id ? { ...image, ...changes } : image));
  };

  const setCover = (id: string) => {
    setPendingImages((current) => current.map((image) => ({ ...image, isCover: image.id === id })));
  };

  const moveImage = (index: number, direction: -1 | 1) => {
    setPendingImages((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const getUploadStartOrder = async () => {
    const { data, error } = await supabase
      .from('gallery_images')
      .select('display_order')
      .eq('event_id', eventId)
      .order('display_order', { ascending: false })
      .limit(1);
    if (error) throw error;
    return ((data?.[0]?.display_order ?? -1) as number) + 1;
  };

  const eventHasCover = async () => {
    const { data, error } = await supabase
      .from('gallery_images')
      .select('id')
      .eq('event_id', eventId)
      .eq('is_cover', true)
      .limit(1);
    if (error) throw error;
    return (data || []).length > 0;
  };

  const handleUploadAll = async () => {
    if (pendingImages.length === 0) return;
    const imagesToUpload = [...pendingImages];
    const successfulIds: string[] = [];
    const uploadedUrls: string[] = [];

    try {
      setUploading(true);
      setUploadStatus('Preparing upload...');
      const startOrder = await getUploadStartOrder();
      const pendingCoverId = imagesToUpload.find((image) => image.isCover)?.id;
      const coverId = pendingCoverId || (!(await eventHasCover()) ? imagesToUpload[0].id : null);

      if (coverId) {
        const { error } = await supabase.from('gallery_images').update({ is_cover: false }).eq('event_id', eventId);
        if (error) throw error;
      }

      for (const [index, image] of imagesToUpload.entries()) {
        setUploadStatus(`Uploading ${index + 1} of ${imagesToUpload.length}: ${image.file.name}`);
        const fileExt = image.file.name.split('.').pop();
        const filePath = `${crypto.randomUUID()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage.from('gallery_images').upload(filePath, image.file);
        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage.from('gallery_images').getPublicUrl(filePath);

        const { error: dbError } = await supabase.from('gallery_images').insert({
          event_id: eventId,
          image_url: publicUrl,
          is_cover: image.id === coverId,
          alt_text: image.altText.trim() || image.file.name,
          caption_en: image.captionEn.trim() || null,
          caption_ko: image.captionKo.trim() || null,
          display_order: startOrder + index,
        });

        if (dbError) {
          await supabase.storage.from('gallery_images').remove([filePath]);
          throw dbError;
        }

        successfulIds.push(image.id);
        uploadedUrls.push(publicUrl);
      }

      // Bake permanent WebP thumbnails in the background (best-effort). Cards
      // still render via the onError fallback chain if a bake hasn't finished.
      void Promise.allSettled(uploadedUrls.map((url) => generateAndStoreThumbnail(url)));

      toast({ title: 'Success', description: `${imagesToUpload.length} image(s) uploaded successfully.` });
      clearAll();
      onUploadComplete?.();
    } catch (error) {
      if (successfulIds.length > 0) {
        setPendingImages((current) => current.filter((image) => !successfulIds.includes(image.id)));
        onUploadComplete?.();
      }

      toast({
        title: 'Upload failed',
        description: successfulIds.length > 0
          ? `${successfulIds.length} image(s) uploaded before failure. ${error.message}`
          : error.message,
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      setUploadStatus('');
    }
  };

  return (
    <div className="space-y-4 rounded-lg border bg-white p-4">
      <div
        className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${isDragActive ? 'border-primary bg-primary/5' : 'border-gray-200 bg-gray-50'}`}
        onDragOver={(event) => { event.preventDefault(); setIsDragActive(true); }}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={(event) => { event.preventDefault(); setIsDragActive(false); addFiles(event.dataTransfer.files); }}
      >
        <UploadCloud className="mx-auto mb-3 h-8 w-8 text-primary" />
        <Label htmlFor="gallery-batch-upload" className="cursor-pointer font-semibold text-primary">
          Drag & drop images here, or click to select
        </Label>
        <Input
          id="gallery-batch-upload"
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          onChange={(event) => { if (event.target.files) addFiles(event.target.files); event.target.value = ''; }}
          disabled={uploading}
          className="sr-only"
        />
        <p className="mt-2 text-xs text-muted-foreground">JPG, PNG, or WebP. Max 2MB each. Queue order is saved as image sequence.</p>
      </div>

      {pendingImages.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h4 className="font-semibold">Pending upload queue</h4>
              <p className="text-sm text-muted-foreground">
                Review, reorder, choose cover, and add captions. Upload all images using the action button below the list.
              </p>
            </div>
          </div>

          {uploadStatus && (
            <div className="rounded-md bg-primary/5 px-3 py-2 text-sm text-primary">
              {uploadStatus}
            </div>
          )}

          <div className="space-y-4">
            {pendingImages.map((image, index) => (
              <div key={image.id} className="grid gap-4 rounded-lg border bg-white p-3 shadow-sm md:grid-cols-[180px_1fr]">
                <div className="relative overflow-hidden rounded-md border bg-muted">
                  <img src={image.previewUrl} alt="Pending preview" className="h-36 w-full object-cover" />
                  <div className="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-1 text-xs font-medium text-white">#{index + 1}</div>
                  {image.isCover && <div className="absolute right-2 top-2 rounded-full bg-yellow-400 px-2 py-1 text-xs font-medium text-black">Cover</div>}
                </div>
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Image className="h-4 w-4" />{image.file.name} ({(image.file.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                    <div className="flex gap-1">
                      <Button type="button" variant="outline" size="icon" onClick={() => moveImage(index, -1)} disabled={uploading || index === 0}><ArrowUp className="h-4 w-4" /></Button>
                      <Button type="button" variant="outline" size="icon" onClick={() => moveImage(index, 1)} disabled={uploading || index === pendingImages.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                      <Button type="button" variant={image.isCover ? 'default' : 'outline'} size="icon" onClick={() => setCover(image.id)} disabled={uploading} title="Set as cover"><Star className="h-4 w-4" /></Button>
                      <Button type="button" variant="destructive" size="icon" onClick={() => removeImage(image.id)} disabled={uploading}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label>Alt Text</Label>
                      <Input value={image.altText} onChange={(event) => updateImage(image.id, { altText: event.target.value })} disabled={uploading} />
                    </div>
                    <div className="space-y-2">
                      <Label>Caption (English)</Label>
                      <Textarea value={image.captionEn} onChange={(event) => updateImage(image.id, { captionEn: event.target.value })} disabled={uploading} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Caption (Korean)</Label>
                      <Textarea value={image.captionKo} onChange={(event) => updateImage(image.id, { captionKo: event.target.value })} disabled={uploading} rows={2} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 border-t bg-white/95 py-4 backdrop-blur">
            <p className="text-sm text-muted-foreground">
              Ready to upload {pendingImages.length} image{pendingImages.length === 1 ? '' : 's'} in the sequence shown above.
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={clearAll} disabled={uploading}>Clear All</Button>
              <Button type="button" onClick={handleUploadAll} disabled={uploading}>
                {uploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {uploading ? 'Uploading...' : `Upload All Images (${pendingImages.length})`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;